/**
 * Data processor for the `wsolution_dashboard` Elasticsearch config query.
 *
 * Shapes the `size:0` aggregation envelope into ready-to-render Chart.js
 * datasets + a status-family proportion bar, so the WSolution dashboard page
 * renders 100% declaratively in YAML (the `ChartJs` primitive + plain divs) —
 * no presentational React component. All the client-side concerns YAML can't
 * express (stable per-code colours, histogram bucket label formatting, dataset
 * assembly, the family proportion bar, emptiness flags) are baked here.
 *
 * Runs for both the initial GET and every POST refetch — matched by URL. The
 * histogram `interval` travels in the URL query string (it drives the ES
 * `fixed_interval`), so it's read from `requestContext.url` to format labels.
 */

import { isIncomingWsid, othersLabel, wsidHost, wsolutionHrefs, wsolutionParams } from './wsolutionWsid';

// HTTP status family → semantic colour (mirrors the logs/calls tables).
const FAMILY_COLORS = { 2: '#10b981', 3: '#3b82f6', 4: '#f59e0b', 5: '#ef4444' };
const FALLBACK_COLOR = '#9ca3af';

const PERF_COLOR = '#7c3aed';
const PERF_FILL = 'rgba(124,58,237,0.12)';

const INTERVAL_LABELS = {
  '15m': '15 minutes', '1h': '1 heure', '3h': '3 heures',
  '1d': '1 jour', '7d': '7 jours',
};

function familyOf(code) {
  return Math.floor(Number(code) / 100);
}

/** Lightens a hex colour towards white by `percent` (0..1). */
function shade(hex, percent) {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  r = Math.round(r + (255 - r) * percent);
  g = Math.round(g + (255 - g) * percent);
  b = Math.round(b + (255 - b) * percent);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Builds a stable `code → colour` map: each code keeps its family hue, and
 * codes sharing a family are progressively lightened so the donut/line stay
 * readable (e.g. 200 darker green, 201 lighter green).
 */
function buildCodeColors(codes) {
  const byFamily = {};
  const map = {};
  const sorted = [...codes].sort((a, b) => Number(a) - Number(b));
  for (const code of sorted) {
    const fam = familyOf(code);
    const idx = byFamily[fam] = (byFamily[fam] ?? -1) + 1;
    map[code] = shade(FAMILY_COLORS[fam] ?? FALLBACK_COLOR, Math.min(idx * 0.18, 0.6));
  }
  return map;
}

const DAY_MS = 86_400_000;

/** Jours couverts par un `fixed_interval` ES (`15m`, `3h`, `7d`) ; 0 si infra-journalier. */
function intervalDays(interval) {
  const m = /^(\d+)([mhd])$/.exec(String(interval ?? ''));
  return m && m[2] === 'd' ? Number(m[1]) : 0;
}

/**
 * Étiquette d'un paquet d'histogramme.
 *
 * Heure pour les pas infra-journaliers, date pour le pas d'un jour, et
 * INTERVALLE dès que le paquet couvre plusieurs jours : étiqueté par son seul
 * premier jour, il ment sur ce qu'il contient. Constaté sur le tableau de bord
 * WSolution — un pas de 7 jours, un point annoncé « 17/09 » qui agrégeait en
 * fait le 17 au 23, et le lecteur cherchant en vain le 18 qu'il venait de
 * demander. Les paquets ES `fixed_interval` sont d'ailleurs calés sur l'époque
 * Unix, pas sur la période choisie : rien dans l'écran ne permettait de le
 * deviner.
 */
function formatBucket(epochMs, interval) {
  const days = intervalDays(interval);
  const start = new Date(epochMs);
  if (days === 0) {
    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(start);
  }
  if (days === 1) {
    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(start);
  }
  const dayMonth = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit' });
  return `${dayMonth.format(start)} – ${dayMonth.format(new Date(epochMs + (days - 1) * DAY_MS))}`;
}

/**
 * Resolves the histogram `interval` for label formatting. It rides the query
 * string on the initial GET (`additionalDataSource` builds it) and the POST
 * body on refetch (`submitData data: { interval: ~~.interval }`, since a static
 * submitData URL can't carry query params). Default `1d`.
 */
function resolveInterval(requestContext) {
  const body = requestContext?.body;
  if (body && typeof body === 'object') {
    const fromBody = body.interval ?? body.es_query?.interval;
    if (fromBody) return String(fromBody);
  }
  try {
    const u = new URL(requestContext?.url ?? '', window.location.origin);
    return u.searchParams.get('interval') || u.searchParams.get('es_query[interval]') || '1d';
  } catch {
    return '1d';
  }
}

/** Calls-over-time line: one dataset per HTTP code, coloured by family. */
function buildCallsData(agg, interval, codeColors) {
  const buckets = agg?.calls_over_time?.buckets ?? [];
  const codes = (agg?.status_detail?.buckets ?? [])
    .map((b) => String(b.key)).sort((a, b) => Number(a) - Number(b));
  const labels = buckets.map((b) => formatBucket(b.key, interval));
  const datasets = codes.map((code) => ({
    label: code,
    data: buckets.map((b) => {
      const cb = (b.by_code?.buckets ?? []).find((x) => String(x.key) === code);
      return cb ? cb.doc_count : 0;
    }),
    borderColor: codeColors[code],
    backgroundColor: codeColors[code],
    tension: 0.3,
    pointRadius: buckets.length > 40 ? 0 : 2,
    borderWidth: 2,
  }));
  return { labels, datasets };
}

/** Average-response-time line (µs → ms). */
function buildPerfData(agg, interval) {
  const buckets = agg?.perf_over_time?.buckets ?? [];
  return {
    labels: buckets.map((b) => formatBucket(b.key, interval)),
    datasets: [{
      label: 'Temps de réponse moyen',
      data: buckets.map((b) => (b.avg_duration?.value != null ? Math.round(b.avg_duration.value / 1000) : null)),
      borderColor: PERF_COLOR,
      backgroundColor: PERF_FILL,
      fill: true,
      tension: 0.3,
      spanGaps: true,
      pointRadius: buckets.length > 40 ? 0 : 2,
      borderWidth: 2,
    }],
  };
}

/** Per-code doughnut (200 / 404 / …). */
function buildDetailData(agg, codeColors) {
  const buckets = (agg?.status_detail?.buckets ?? []).filter((b) => b.doc_count > 0);
  return {
    labels: buckets.map((b) => String(b.key)),
    datasets: [{
      data: buckets.map((b) => b.doc_count),
      backgroundColor: buckets.map((b) => codeColors[String(b.key)] ?? FALLBACK_COLOR),
      borderWidth: 1,
      borderColor: '#fff',
    }],
  };
}

/**
 * Status-family proportion bar (2xx/3xx/4xx/5xx): each segment carries a
 * ready-to-bind inline `style` (width % + colour) + a swatch style + tooltip,
 * so the YAML renders the bar + legend with plain divs (cf. wsolutionCalls).
 */
function buildFamily(agg) {
  const order = ['2xx', '3xx', '4xx', '5xx'];
  const byKey = Object.fromEntries((agg?.status_family?.buckets ?? []).map((b) => [b.key, b.doc_count]));
  const total = order.reduce((sum, k) => sum + (byKey[k] ?? 0), 0);
  const items = order
    .map((k) => ({ key: k, count: byKey[k] ?? 0, color: FAMILY_COLORS[k[0]] ?? FALLBACK_COLOR }))
    .filter((it) => it.count > 0)
    .map((it) => {
      const pct = total ? (it.count / total) * 100 : 0;
      return {
        key: it.key,
        count: it.count,
        countFormatted: it.count.toLocaleString('fr-FR'),
        pct,
        pctLabel: `${pct.toFixed(1)} %`,
        barLabel: pct >= 8 ? `${Math.round(pct)} %` : '',
        style: { width: `${pct}%`, backgroundColor: it.color },
        swatchStyle: { backgroundColor: it.color },
        title: `${it.key} — ${it.count.toLocaleString('fr-FR')} (${pct.toFixed(1)} %)`,
      };
    });
  return { items, total, totalFormatted: total.toLocaleString('fr-FR') };
}

/**
 * Palmarès d'une agrégation `terms` : liste triée, prête à rendre en barres.
 *
 * Sert les deux axes qui n'ont de sens que sur un webservice ENTRANT — d'où
 * viennent les appels (adresse de l'appelant) et de quelle page (référent). Le
 * référent est absent de la plupart des appels (machine à machine) : plutôt que
 * de faire disparaître ces appels du palmarès, ils sont regroupés sous un
 * libellé explicite, sans quoi le total de la carte contredirait celui des
 * graphes voisins.
 *
 * @param {object} bucketsOwner
 *   L'agrégation ES (`{buckets, sum_other_doc_count}`), ou undefined.
 * @param {number} grandTotal
 *   Nombre total d'appels sur la période, pour la part de chaque ligne.
 * @param {object} [options]
 *   - missing {{label, count}|null} : le lot d'appels auxquels le champ manque,
 *     compté par une agrégation `missing` côté serveur.
 *   - distinct {number|null} : nombre d'éléments DISTINCTS (agrégation
 *     `cardinality`), pour dire « 2 994 autres adresses » plutôt qu'un reste muet.
 *   - noun {[string, string]} : ['adresse', 'adresses'].
 */
function buildTopList(bucketsOwner, grandTotal, { missing = null, distinct = null, noun = ['élément', 'éléments'] } = {}) {
  const buckets = bucketsOwner?.buckets ?? [];
  const items = buckets
    .map((b) => ({ key: String(b.key), count: b.doc_count ?? 0 }))
    .filter((it) => it.count > 0);

  // Ce que le top-N a laissé de côté. Sans cette ligne, les parts affichées ne
  // somment pas à 100 % et rien ne le dit à qui lit.
  const truncated = bucketsOwner?.sum_other_doc_count ?? 0;
  if (truncated > 0) {
    items.push({
      key: othersLabel(distinct, buckets.length, noun) ?? `Autres (hors des ${buckets.length} premiers)`,
      count: truncated,
      muted: true,
      // Le reste d'un palmarès va TOUJOURS en dernier : avec des milliers d'IP il
      // pèse plus que chacune, et trié au volume il passait en tête de liste.
      remainder: true,
    });
  }

  // Le nombre d'appels SANS le champ vient d'une agrégation `missing` dédiée,
  // jamais d'une soustraction sur le total : soustraire compterait AUSSI la
  // queue tronquée ci-dessus comme « sans référent », et le plafond de
  // `hits.total` suffirait à faire disparaître la ligne.
  if (missing && missing.count > 0) {
    items.push({ key: missing.label, count: missing.count, muted: true });
  }

  // L'échelle des barres se prend sur les lignes NOMMÉES : rapportées au reste
  // (36 748 appels contre 1 504 pour la première IP, mesuré), elles tombaient à
  // quelques pixels ; la barre du reste est plafonnée à la largeur.
  const max = items.filter((it) => !it.remainder).reduce((m, it) => Math.max(m, it.count), 0)
    || items.reduce((m, it) => Math.max(m, it.count), 0);

  return items
    .sort((a, b) => Number(!!a.remainder) - Number(!!b.remainder) || b.count - a.count)
    .map((it) => {
      const pct = grandTotal ? (it.count / grandTotal) * 100 : 0;
      return {
        key: it.key,
        count: it.count,
        countFormatted: it.count.toLocaleString('fr-FR'),
        pctLabel: `${pct.toFixed(1)} %`,
        // Largeur relative au PLUS GROS, pas au total : une liste dont la tête
        // pèse 3 % resterait sinon une rangée de traits invisibles.
        barStyle: { width: `${max ? Math.min(100, (it.count / max) * 100) : 0}%` },
        labelClass: it.muted
          ? 'truncate text-gray-400 italic'
          : 'truncate text-gray-700',
      };
    });
}

export const wsolutionDashboardProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  // Deux écrans, un seul formatage : /mes-cles-api/appels/tableau-de-bord sert
  // les MÊMES documents `analyzable_content` avec les mêmes agrégations, sous
  // un filtre différent. Élargir ce garde vaut mieux que dupliquer la mise en
  // forme des datasets Chart.js.
  const url = requestContext?.url ?? '';
  if (!url.includes('/config/wsolution_dashboard') && !url.includes('/config/api_key_dashboard')) return dataToProcess;
  if (responseContext?.status >= 400) return dataToProcess;
  if (!dataToProcess || typeof dataToProcess !== 'object') return dataToProcess;

  const agg = dataToProcess.aggregations ?? {};
  const interval = resolveInterval(requestContext);
  const codeColors = buildCodeColors((agg?.status_detail?.buckets ?? []).map((b) => String(b.key)));

  const calls = buildCallsData(agg, interval, codeColors);
  const perf = buildPerfData(agg, interval);
  const detail = buildDetailData(agg, codeColors);
  const family = buildFamily(agg);

  const wsid = new URLSearchParams(window.location.search).get('wsid') ?? '';

  // Sur un webservice ENTRANT, la destination est toujours le site : la seule
  // information qui distingue un appel d'un autre est son APPELANT. Les deux
  // cartes ci-dessous ne sont donc calculées — et rendues — que dans ce sens.
  const incoming = isIncomingWsid(wsid);
  // `metadata.total` vient de `hits.total.value`, que la requête débride avec
  // `track_total_hits` : sans ce réglage Elasticsearch plafonne à 10 000 et les
  // parts affichées seraient calculées sur un total faux, sans rien signaler.
  const totalCalls = dataToProcess?.metadata?.total ?? family.total;
  const callers = incoming
    ? buildTopList(agg.by_initiator_addr, totalCalls, {
      distinct: agg.distinct_initiator_addr?.value,
      noun: ['adresse', 'adresses'],
    })
    : [];
  const referers = incoming
    ? buildTopList(agg.by_initiator_referer, totalCalls, {
      missing: { label: 'Accès direct (sans référent)', count: agg.missing_referer?.doc_count ?? 0 },
      distinct: agg.distinct_initiator_referer?.value,
      noun: ['référent', 'référents'],
    })
    : [];

  const hasCalls = calls.datasets.length > 0 && calls.labels.length > 0;
  const hasPerf = perf.labels.length > 0;
  const hasFamily = family.total > 0;
  const hasDetail = detail.labels.length > 0;

  return {
    ...dataToProcess,
    charts: { calls, perf, detail },
    family,
    callers,
    referers,
    flags: { hasCalls, hasPerf, hasFamily, hasDetail, incoming },
    // Presence slots for the YAML's array-`Switch` empty-state idiom (Switch
    // has no usable `noResults`, so we pair two complementary Switches): a
    // single-item `present` list renders the chart once; the inverse `absent`
    // list renders the "Aucune donnée" placeholder. Exactly one is non-empty.
    present: {
      calls: hasCalls ? [{}] : [],
      perf: hasPerf ? [{}] : [],
      family: hasFamily ? [{}] : [],
      detail: hasDetail ? [{}] : [],
      // Le bloc « appelants » n'existe que dans le sens entrant : la liste vide
      // suffit à ne rien rendre, sans condition dans le YAML.
      incoming: incoming ? [{}] : [],
      callers: incoming && callers.length ? [{}] : [],
      referers: incoming && referers.length ? [{}] : [],
    },
    absent: {
      calls: hasCalls ? [] : [{}],
      perf: hasPerf ? [] : [{}],
      family: hasFamily ? [] : [{}],
      detail: hasDetail ? [] : [{}],
      callers: incoming && !callers.length ? [{}] : [],
      referers: incoming && !referers.length ? [{}] : [],
    },
    interval,
    intervalLabel: INTERVAL_LABELS[interval] ?? interval,
    view: {
      wsLabel: wsidHost(wsid) || wsid,
      // Calculés ici plutôt qu'en gabarit PathLink (cf. wsolutionHrefs) : les logs
      // AVEC le préfiltre courant, et ce tableau de bord SANS.
      logsHref: wsolutionHrefs(wsolutionParams(window.location.search)).logsHref,
      unfilteredHref: wsolutionHrefs({ id: wsolutionParams(window.location.search).id, wsid }).dashboardHref,
    },
  };
};
